import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { TbCircleCheck, TbAlertCircle, TbCreditCardPay, TbLoader2 } from "react-icons/tb";
import Popup from "src/components/Popup/Popup";
import { apiRequest, ApiError } from "src/services/apiClient";
import type { SumupReader } from "src/hooks/useSumup";
import "./cardReaderPopup.scss";

const POLL_MS = 2000;
// The reader gives up after about a minute without a card; stop waiting a little after a
// payment that was started could have finished.
const WAIT_MS = 2.5 * 60 * 1000;

type Phase = "loading" | "choose" | "sending" | "waiting" | "paid" | "declined" | "timeout" | "error";

interface Props {
    invoiceId: string;
    readableId: string;
    onClose: () => void;
    /** Called once when the payment is booked — the caller reloads the invoice(s). */
    onPaid: () => void;
}

/**
 * Card payment on the practice's SumUp Solo terminal (chapter 10): sends the invoice's outstanding
 * amount to the reader, then polls until the patient has paid (or the payment failed).
 * With several paired readers, staff pick one first.
 */
export default function CardReaderPopup({ invoiceId, readableId, onClose, onPaid }: Props) {
    const { t } = useTranslation();
    const [phase, setPhase] = useState<Phase>("loading");
    const [readers, setReaders] = useState<SumupReader[]>([]);
    const [sent, setSent] = useState<{ amount: number; readerName: string; readerId: string } | null>(null);
    const [errorCode, setErrorCode] = useState<string | null>(null);
    const paidReported = useRef(false);

    const send = useCallback(async (reader: SumupReader) => {
        setPhase("sending");
        setErrorCode(null);
        try {
            const res = await apiRequest<{ amount: number; readerName: string }>(
                `/sumup/invoices/${invoiceId}/reader-checkout`,
                { method: "POST", body: JSON.stringify({ readerId: reader.id }) },
                { "Content-Type": "application/json" },
            );
            setSent({ ...res, readerId: reader.id });
            setPhase("waiting");
        } catch (e) {
            setErrorCode(e instanceof ApiError ? e.body?.code ?? null : null);
            setPhase("error");
        }
    }, [invoiceId]);

    useEffect(() => {
        let cancelled = false;
        apiRequest<{ readers: SumupReader[] }>(`/sumup/invoices/${invoiceId}/readers`)
            .then((res) => {
                if (cancelled) return;
                setReaders(res.readers);
                if (res.readers.length === 1) void send(res.readers[0]);
                else setPhase("choose");
            })
            .catch(() => { if (!cancelled) setPhase("error"); });
        return () => { cancelled = true; };
    }, [invoiceId, send]);

    useEffect(() => {
        if (phase !== "waiting") return;
        const giveUp = window.setTimeout(() => setPhase("timeout"), WAIT_MS);
        const poll = window.setInterval(async () => {
            try {
                const res = await apiRequest<{ status: "pending" | "paid" | "failed" }>(
                    `/sumup/invoices/${invoiceId}/reader-checkout/sync`, { method: "POST" });
                if (res.status === "paid") setPhase("paid");
                else if (res.status === "failed") setPhase("declined");
            } catch {
                // Transient — the next poll (or the webhook / safety-net cron) picks it up.
            }
        }, POLL_MS);
        return () => { window.clearTimeout(giveUp); window.clearInterval(poll); };
    }, [phase, invoiceId]);

    useEffect(() => {
        if (phase !== "paid" || paidReported.current) return;
        paidReported.current = true;
        onPaid();
    }, [phase, onPaid]);

    const cancelAtReader = async () => {
        try { await apiRequest(`/sumup/invoices/${invoiceId}/reader-checkout/cancel`, { method: "POST" }); } catch { /* reader unreachable — it times out by itself */ }
    };

    // Sending again after "no answer" first releases the waiting payment (it's still checked in the background).
    const retry = async () => {
        const reader = readers.find((r) => r.id === sent?.readerId) ?? (readers.length === 1 ? readers[0] : null);
        if (phase === "timeout") await cancelAtReader();
        if (reader) void send(reader);
        else setPhase("choose");
    };

    const amount = sent ? sent.amount.toFixed(2) : "";
    const errorText = errorCode === "READER_OFFLINE" ? t("cardReader.offline")
        : errorCode === "READER_BUSY" || errorCode === "IN_PROGRESS" ? t("cardReader.busy")
            : errorCode === "NOTHING_DUE" ? t("cardReader.nothingDue")
                : t("cardReader.failed");

    const close = { label: t("popup.close"), variant: "default" as const, onClick: onClose };
    const actions =
        phase === "waiting" ? [{ label: t("cardReader.cancel"), variant: "default" as const, onClick: async () => { await cancelAtReader(); onClose(); } }]
            : phase === "paid" ? [{ ...close, variant: "primary" as const }]
                : phase === "declined" || phase === "timeout" || (phase === "error" && readers.length > 0 && errorCode !== "NOTHING_DUE")
                    ? [close, { label: t("cardReader.sendAgain"), variant: "primary" as const, onClick: () => void retry() }]
                    : [close];

    return (
        <Popup
            title={t("cardReader.title", { id: readableId })}
            className="card-reader-popup"
            onClose={onClose}
            message={
                <div className="card-reader" aria-live="polite">
                    {(phase === "loading" || phase === "sending") && (
                        <div className="card-reader__result">
                            <TbLoader2 className="card-reader__spin" />
                            <span>{phase === "sending" ? t("cardReader.sending") : t("cardReader.loading")}</span>
                        </div>
                    )}
                    {phase === "choose" && (
                        <div className="card-reader__choose">
                            <p className="card-reader__hint">{t("cardReader.choose")}</p>
                            {readers.map((r) => (
                                <button key={r.id} type="button" className="card-reader__reader" onClick={() => void send(r)}>
                                    <TbCreditCardPay /> {r.name}
                                </button>
                            ))}
                        </div>
                    )}
                    {phase === "waiting" && sent && (
                        <>
                            <TbCreditCardPay className="card-reader__icon" />
                            <span className="card-reader__amount">{t("cardQr.amount", { amount })}</span>
                            <p className="card-reader__hint">{t("cardReader.hint", { name: sent.readerName })}</p>
                            <p className="card-reader__waiting">{t("cardQr.waiting")}</p>
                        </>
                    )}
                    {phase === "paid" && (
                        <div className="card-reader__result card-reader__result--paid">
                            <TbCircleCheck />
                            <span>{t("cardQr.paid", { amount })}</span>
                        </div>
                    )}
                    {phase === "declined" && (
                        <div className="card-reader__result card-reader__result--error">
                            <TbAlertCircle />
                            <span>{t("cardReader.declined")}</span>
                        </div>
                    )}
                    {phase === "timeout" && (
                        <div className="card-reader__result">
                            <TbAlertCircle />
                            <span>{t("cardReader.timeout")}</span>
                        </div>
                    )}
                    {phase === "error" && (
                        <div className="card-reader__result card-reader__result--error">
                            <TbAlertCircle />
                            <span>{errorText}</span>
                        </div>
                    )}
                </div>
            }
            actions={actions}
        />
    );
}
